<template>
  <div
    class="wt-story-rcp"
    :class="{
      '-playable': isPlayable,
      '-hide-control-panel': hideControlPanel,
    }"
    @mousemove="$_onMouseMove"
    @mouseleave="$_onMouseLeave"
  >
    <div ref="elRcp" class="-rcp">
      <canvas for="player" />
      <canvas for="movie-processor" />
      <canvas for="diagnostic" />

      <img
        v-if="showLogoBugImage"
        :src="logoBugImageURL"
        for="bug-logo"
        alt=""
      />

      <div for="buffer-indicator">
        <img
          src="https://dp4stoslb9jdl.cloudfront.net/Resources/studio/img/instant-player/preloader.svg"
          alt=""
        />
      </div>

      <img
        for="pause-indicator"
        alt="Play"
        src="/resources/img/play-icon-02.svg"
      />
      <img
        for="play-indicator"
        alt="Pause"
        src="/resources/img/pause-icon-02.svg"
      />

      <div
        class="wt-player-curtains animated"
        :aspect-ratio="themeInfo.aspectRatio"
      />
      <div id="instant-preview-progress" class="wt-instant-preview"></div>
    </div>

    <div ref="elRcpPanel" class="rcp-panel" v-show="isPlayable" />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapState } from 'vuex';
import RcpControlPanel from '../assetModal/RcpControlPanel.vue';
import storyViewBus from './storyViewBus';
import app from 'app';
import RCP from '@wochitnpm/rcp';

export default Vue.extend({
  name: 'wtStoryRcp',

  data() {
    return {
      hideControlPanel: false,
      hideControlPanelTimer: new app.namespace.Util.Timer(() => {
        this.hideControlPanel = true;
      }, app.TIME.second(3)),
      aspectRatioChangeTimer: new app.namespace.Util.Timer(() => {
        // this delay is for the animation of the curtains.
        const ratio = app.ASPECT_RATIOS[this.themeInfo.aspectRatio].ratio;
        this.rcp.setInnerAspectRatio(ratio);
      }, app.TIME.second(0.5)),
    };
  },

  computed: {
    isPlayable() {
      return this.timelineDuration > 0;
    },

    showLogoBugImage() {
      return this.isPlayable && this.logoBugImageURL;
    },

    logoBugImageURL() {
      return app.prependHttp(this.themeInfo.logoBugImageURL);
    },

    ...mapState({
      storyBackgroundMusic: (state) => state.story.storyBackgroundMusic,
      timelineDuration: (state) => state.story.timelineDuration,
      themeInfo: (state) => state.story.themeInfo,
    }),
  },

  watch: {
    storyBackgroundMusic() {
      if (this.rcp) {
        this.rcp.updateBGMusic(app.timelineGateway.createPlayerBGMusicTrack());
      }
    },

    timelineDuration() {
      if (this.rcpPanel) {
        this.rcpPanel.time.total = this.timelineDuration;
      }
    },

    themeInfo: '$_themeInfoChanged',
  },

  beforeDestroy() {
    app.keyboardAccessibility.remove(app.KEYBOARD_LAYER.RCP);
    storyViewBus.neverMind(this);
    this.rcp = this.rcp.destroy();
    this.rcpPanel = this.rcpPanel.$destroy();
    this.hideControlPanelTimer.stop();
    this.aspectRatioChangeTimer.stop();
    window.rcp = null;
  },

  mounted() {
    this.rcp = new RCP({
      el: this.$refs.elRcp,
      showDiagnostic: !!0,
      innerAspectRatio: app.ASPECT_RATIOS[this.themeInfo.aspectRatio].ratio,
    });
    if (!app.rootStore.getters['user/box'].isProd()) {
      window.rcp = this.rcp;
    }
    app.setCommonRCPVolume(this.rcp);

    const total = this.timelineDuration;
    this.rcpPanel = new RcpControlPanel({
      el: this.$refs.elRcpPanel,
      type: 'lite',
      data() {
        return {
          time: {
            total,
            offset: 0,
            seekable: true,
          },
        };
      },
      rcp: this.rcp,
    });

    app.timelineGateway.whenWidgetReady.then(() => {
      this.rcp.update({ feed: app.timelineGateway.storyToPlayer() });

      this.rcp.on(RCP.MSG.PLAY, (time) => {
        storyViewBus.$emit('svbRcpIsPlaying', { time, isPlaying: true });
      });

      this.rcp.on(RCP.MSG.PROGRESS, (time) => {
        const isPlaying = this.rcp.isPlaying();
        storyViewBus.$emit('svbRcpCurrentTime', {
          time,
          updateSecondhand: isPlaying,
        });
      });

      this.rcp.on(RCP.MSG.PAUSE, (time) => {
        storyViewBus.$emit('svbRcpIsPlaying', { time, isPlaying: false });
      });

      this.rcp.on(RCP.MSG.BUG, (bug) => {
        app.doctorController.report('RCP ' + String(bug), bug);
      });

      this.rcpPanel.$on('seek', ({ time, e }) => {
        storyViewBus.$emit('svbRcpCurrentTime', {
          time,
          updateSecondhand: true,
        });
      });

      app.keyboardAccessibility.add(app.KEYBOARD_LAYER.RCP, [
        {
          on: 'keydown',
          key: $.ui.keyCode.SPACE,
          run: this.$_togglePlay,
        },
        {
          on: 'keydown',
          key: $.ui.keyCode.SPACE,
          shiftKey: true,
          run: this.$_togglePlay,
        },
      ]);
    });

    storyViewBus.mind(this, [
      'svbPauseRcp',
      'svbSeekRcp',
      'svbUpdateRcp',
      'svbSceneEnterRcp',
      'svbSceneUpdateRcp',
      'svbSceneLeaveRcp',
      'svbBeforeSneakPeekRcp',
      'svbDuringSneakPeekRcp',
      'svbAfterSneakPeek',
    ]);
  },

  methods: {
    $_onMouseMove() {
      this.hideControlPanel = false;
      this.hideControlPanelTimer.restart();
    },

    $_onMouseLeave() {
      this.hideControlPanelTimer.stop();
      this.hideControlPanel = false;
    },

    $_themeInfoChanged() {
      this.rcp.pause();
      const ratio = app.ASPECT_RATIOS[this.themeInfo.aspectRatio].ratio;
      const oldRatio = this.rcp.getInnerAspectRatio();
      this.aspectRatioChangeTimer.setDelay(
        app.TIME.second(oldRatio < ratio ? 0 : 0.5)
      );
      this.aspectRatioChangeTimer.restart();
    },

    $_togglePlay: function (e) {
      if (!app.abortKeyboard(e) && this.rcp) {
        e.preventDefault(); // prevent scroll
        this.rcpPanel.$emit('togglePlay', e.shiftKey);
      }
    },

    svbPauseRcp() {
      this.rcp.pause();
    },

    svbSeekRcp(time, seekImmediate = false) {
      if (seekImmediate) {
        this.rcp.seekImmediate(time);
      } else {
        this.rcp.seek(time);
      }
    },

    svbUpdateRcp(options) {
      this.rcp.update(options);
    },

    svbSceneEnterRcp({ track, seekToTime }) {
      const feed = app.timelineGateway.storyToPlayer();

      const trackIndex = feed.visual.findIndex((plItem) => {
        return track.play.from === plItem.play.from;
      });
      feed.visual[trackIndex] = track;

      /**
       * this is a HACK
       *
       * the last frame (a.k.a. play.until) of the previous pTrack overcome
       * the first frame (a.k.a. play.from) of the selected pTrack
       *
       * the hack is to remove 1e-3 from the last frame of the previous pTrack
       * and from the selected first frame in order to remove the last frame of
       * the previous pTrack.
       */
      if (trackIndex > 0) {
        feed.visual[trackIndex - 1].play.until -= 1e-3;
        feed.visual[trackIndex].play.from -= 1e-3;
      }

      this.rcp.pause();
      this.rcp.update({ feed, seekToTime });
      this.rcpPanel.$data.time.total = track.play.duration;
      this.rcpPanel.$data.time.offset = track.play.from;
    },

    svbSceneUpdateRcp({ track }) {
      const feed = app.timelineGateway.storyToPlayer();
      feed.visual = feed.visual.map((plItem) => {
        return track.play.from === plItem.play.from ? track : plItem;
      });
      this.rcp.pause();
      this.rcp.update({ feed });
    },

    svbSceneLeaveRcp(options) {
      this.rcp.update(options);
      this.rcpPanel.$data.time.total = this.timelineDuration;
      this.rcpPanel.$data.time.offset = 0;
    },

    svbBeforeSneakPeekRcp(track) {
      this.rcp.beforeSneakPeek(track);
    },

    svbDuringSneakPeekRcp(time) {
      this.rcp.sneakPeek(time);
    },

    svbAfterSneakPeek() {
      this.rcp.afterSneakPeek();
    },
  },
});
</script>

<style lang="scss">
@import 'resources/css/bootstrap/assets/stylesheets/bootstrap/mixins/_size.scss';

.wt-story-rcp {
  $player-width: 560px;
  $player-height: 315px;
  $canvas_z_index: 1;
  $curtain_anim: width 0.5s ease-out;
  $curtain_padding_fix: 2px;

  width: $player-width;
  height: $player-height;
  background-color: #273038;
  position: relative;

  &.-playable {
    cursor: pointer;

    &.-hide-control-panel {
      .wt2-player-control-panel.-playing {
        opacity: 0;
      }
    }

    /*
      visible by default
      hidden only when video plays AND mouse not hovering player area.
    */
    .wt2-player-control-panel {
      opacity: 1;
    }
    &:not(:hover) .wt2-player-control-panel.-playing {
      opacity: 0;
    }
  }

  &:hover {
    .wt2-player-control-panel {
      transition-delay: 0s;
    }
  }

  .wt2-player-control-panel {
    z-index: $canvas_z_index + 4;
  }

  .-rcp {
    width: $player-width;
    height: $player-height;
    margin: 0 auto;
    position: relative;
    @include user-select(none);
    z-index: 0; // reset current index so internal elements with higher index wont overlap his siblings

    canvas[for='player'] {
      box-shadow: inset 0 0 5px rgba(0, 0, 0, 0.4);
      height: 100%;
      width: 100%;
      position: absolute;
      z-index: $canvas_z_index;
    }

    canvas[for='diagnostic'] {
      pointer-events: none;
      position: absolute;
      visibility: hidden;
      opacity: 0;
      transition: visibility 0.5s, opacity 0.2s linear;
      z-index: $canvas_z_index + 2;
    }

    canvas[for='movie-processor'] {
      pointer-events: none;
      position: absolute;
      z-index: $canvas_z_index + 3;
    }

    *[for='buffer-indicator'] {
      @include center();
      z-index: $canvas_z_index + 2;
    }

    *[for='bug-logo'] {
      height: 100%;
      pointer-events: none;
      @include center();
      z-index: $canvas_z_index + 1;
      max-width: 100%;

      &.wt-hidden {
        display: none;
      }
    }

    *[for='pause-indicator'],
    *[for='play-indicator'] {
      $play-button-size: 50px;

      cursor: pointer;
      visibility: hidden;
      pointer-events: none;
      @include square($play-button-size);
      @include center();
      z-index: $canvas_z_index + 3;
      &.animated {
        transition: opacity 0.5s ease-in, transform 0.5s ease-in;
        opacity: 0;
        transform: translate(-50%, -50%) scale(1.5);
      }
    }
  }

  .wt-player-curtains {
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    z-index: $canvas_z_index + 1;
    pointer-events: none;

    &:after,
    &:before {
      height: 100%;
      width: 0%;
      background: $black;
      content: '';
      position: absolute;
    }

    &:before {
      left: 0;
    }
    &:after {
      right: 0;
    }

    &.animated {
      &:after,
      &:before {
        transition: $curtain_anim;
      }
    }

    &[aspect-ratio='ASPECT_4_5'] {
      &:after,
      &:before {
        width: calc((#{$player-width} - (#{$player-height} * (4 / 5))) / 2);
      }
    }

    &[aspect-ratio='ASPECT_1_1'] {
      &:after,
      &:before {
        width: calc((#{$player-width} - #{$player-height}) / 2);
      }
    }

    &[aspect-ratio='ASPECT_9_16'] {
      &:after,
      &:before {
        width: calc((#{$player-width} - (#{$player-height} * (9 / 16))) / 2);
      }
    }

    &[aspect-ratio='ASPECT_3_4'] {
      &:after,
      &:before {
        width: calc((#{$player-width} - (#{$player-height} * (3 / 4))) / 2);
      }
    }

    &[aspect-ratio='ASPECT_3_2'] {
      &:after,
      &:before {
        width: calc((#{$player-width} - (#{$player-height} * (3 / 2))) / 2);
      }
    }
  }

  .wt-instant-preview {
    position: absolute;
    height: 100%;
    width: 100%;
    background: rgba(0, 0, 0, 0.55);
    color: $white;
    display: none;
    font-size: 100px;
    pointer-events: none;
    z-index: $canvas_z_index + 2;

    &:after {
      width: 270px;
      content: 'Generating Preview';
      position: absolute;
      display: block;
      left: 50%;
      top: 50%;
      transform: translate(-50%, -50%);
      font-weight: 100;
      font-size: 30px;
      color: $white;
      animation: rotatePreviewDots 3s infinite;

      @keyframes rotatePreviewDots {
        0% {
          content: 'Generating Preview';
        }
        25% {
          content: 'Generating Preview.';
        }
        50% {
          content: 'Generating Preview..';
        }
        75% {
          content: 'Generating Preview...';
        }
        100% {
          content: 'Generating Preview';
        }
      }
    }
  }
}
</style>
